import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/booking-embed.css';

// Media items for the carousel - easily swap these out with your own images/videos
// Pop-up event images including bungee and trapeze
const galleryMedia = [
  { type: 'image', src: '/images/hero/bungee_betsy_katie.jpg', alt: 'Pop-up fitness event' },
  { type: 'image', src: '/images/hero/bungee_cal_star.jpg', alt: 'Bungee pop-up class' },
  { type: 'image', src: 'https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/dosxiw2t_JennaAFTrap1500mb.png', alt: 'Jenna on trapeze at studio' },
  { type: 'image', src: '/images/hero/bungee_trio.jpg', alt: 'Group at pop-up event' },
  { type: 'image', src: '/images/hero/bungee_high5.jpg', alt: 'High five at pop-up' },
];

const BookPopupPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const IMAGE_DURATION = 4000; // 4 seconds for images
  const VIDEO_BUFFER = 500;

  const goToNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryMedia.length);
      setIsTransitioning(false);
    }, 300);
  };

  const goToPrev = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + galleryMedia.length) % galleryMedia.length);
      setIsTransitioning(false);
    }, 300);
  };

  // Handle auto-advancement
  useEffect(() => {
    const currentMedia = galleryMedia[currentIndex];

    if (currentMedia.type === 'image') {
      timerRef.current = setTimeout(goToNext, IMAGE_DURATION);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex]);

  // Handle video end
  const handleVideoEnd = () => {
    setTimeout(goToNext, VIDEO_BUFFER);
  };

  // Reset and play video when it becomes active
  useEffect(() => {
    if (galleryMedia[currentIndex].type === 'video' && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {
        setTimeout(goToNext, 1000);
      });
    }
  }, [currentIndex]);

  // Load Acuity embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://embed.acuityscheduling.com/js/embed.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://embed.acuityscheduling.com/js/embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // Ken Burns effect variations
  const kbEffects = ['kb-zoom-in', 'kb-zoom-out', 'kb-pan-left', 'kb-pan-right'];
  const getKbEffect = (index) => kbEffects[index % kbEffects.length];

  const currentMedia = galleryMedia[currentIndex];

  return (
    <div className="booking-embed-page with-gallery">
      {/* Mini Hero Gallery Carousel */}
      <div className="booking-gallery" data-testid="popup-gallery">
        <div className={`gallery-media ${isTransitioning ? 'transitioning' : ''}`}>
          {currentMedia.type === 'video' ? (
            <video
              ref={videoRef}
              key={currentMedia.src}
              className="gallery-video"
              src={currentMedia.src}
              muted
              playsInline
              onEnded={handleVideoEnd}
            />
          ) : (
            <div
              key={currentMedia.src}
              className={`gallery-image ${getKbEffect(currentIndex)}`}
              style={{ backgroundImage: `url(${currentMedia.src})` }}
            />
          )}
        </div>

        {/* Gallery overlay with title */}
        <div className="gallery-overlay">
          <h1 className="gallery-title">Book Your Pop-Up Here</h1>
          <p className="gallery-subtitle">Catch us around town — limited spots, unlimited energy</p>
        </div>

        {/* Navigation arrows */}
        {galleryMedia.length > 1 && (
          <>
            <button 
              className="gallery-nav-btn prev" 
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              className="gallery-nav-btn next" 
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Progress indicators */}
        <div className="gallery-indicators">
          {galleryMedia.map((_, index) => (
            <button
              key={index}
              className={`gallery-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => {
                if (index !== currentIndex) {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentIndex(index);
                    setIsTransitioning(false);
                  }, 300);
                }
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="booking-embed-header compact">
        <Link to="/events" className="back-link">
          <ArrowLeft size={20} />
          Back to Schedule
        </Link>
      </div>

      <div className="booking-embed-container" data-testid="acuity-embed-container">
        <iframe 
          src="https://app.acuityscheduling.com/schedule.php?owner=37343334&appointmentType=95286342&ref=embedded_csp" 
          title="Book Pop-Up Class" 
          width="100%" 
          height="800" 
          frameBorder="0" 
          allow="payment"
          className="booking-embed-iframe acuity-iframe"
        />
      </div>

      <div className="booking-embed-fallback">
        <p>Having trouble?</p>
        <a 
          href="https://app.acuityscheduling.com/schedule.php?owner=37343334&appointmentType=95286342" 
          target="_blank" 
          rel="noopener noreferrer"
          className="fallback-link"
        >
          Open booking page in a new tab
          <ExternalLink size={16} />
        </a>
      </div>
    </div>
  );
};

export default BookPopupPage;
